import axios, { AxiosResponse } from 'axios';
import {
  User,
  Organization,
  UserTier,
  TierProgressInfo,
  UserAnalytics,
  FeatureFlag,
  TierUpgradeResponse,
  CreateUserForm,
  UpdateUserForm,
  CreateOrganizationForm,
  IdentityVerificationForm,
  PaginatedResponse,
} from '../types/tier';

class TierApiService {
  private baseURL: string;
  private authToken: string | null = null;

  constructor() {
    this.baseURL = import.meta.env.VITE_API_URL || window.location.origin;
  }

  // Token management methods
  setToken(token: string): void {
    this.authToken = token;
  }

  removeToken(): void {
    this.authToken = null;
  }

  private getAuthHeaders(): Record<string, string> {
    try {
      // Use stored token if available, otherwise fall back to session data
      if (this.authToken) {
        return {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.authToken}`,
        };
      }

      const sessionData = localStorage.getItem('mcp_session');
      let session = null;
      
      if (sessionData) {
        try {
          const parsed = JSON.parse(sessionData);
          // Handle both wrapped format (from storage service) and unwrapped format (from auth-api)
          if (parsed && typeof parsed === 'object' && 'data' in parsed) {
            // Wrapped format: { data: {...}, timestamp: ..., version: ... }
            session = parsed.data;
          } else {
            // Unwrapped format: direct session object
            session = parsed;
          }
        } catch (parseError) {
          console.error('Error parsing session data:', parseError);
        }
      }
      
      return {
        'Content-Type': 'application/json',
        ...(session?.sessionId && { Authorization: `Bearer ${session.sessionId}` }),
      };
    } catch (error) {
      console.error('Error getting auth headers:', error);
      return {
        'Content-Type': 'application/json',
      };
    }
  }

  private async request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    endpoint: string,
    data?: any
  ): Promise<T> {
    try {
      const response: AxiosResponse<T> = await axios({
        method,
        url: `${this.baseURL}${endpoint}`,
        data,
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.error || error.message;
        throw new Error(message);
      }
      throw error;
    }
  }

  // User Management
  async getUsers(limit = 50, cursor?: string): Promise<PaginatedResponse<User>> {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (cursor) params.append('cursor', cursor);

    return this.request<PaginatedResponse<User>>('GET', `/api/users?${params}`);
  }

  async getUser(userId: string): Promise<User> {
    return this.request<User>('GET', `/api/users/${userId}`);
  }

  async createUser(userData: CreateUserForm): Promise<User> {
    return this.request<User>('POST', '/api/users', userData);
  }

  async updateUser(userId: string, updates: UpdateUserForm): Promise<User> {
    return this.request<User>('PUT', `/api/users/${userId}`, updates);
  }

  async deleteUser(userId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>('DELETE', `/api/users/${userId}`);
  }

  async searchUsers(query: string): Promise<User[]> {
    const params = new URLSearchParams({ q: query });
    const response = await this.request<PaginatedResponse<User>>('GET', `/api/users?${params}`);
    return response.items;
  }

  // Tier Management
  async getUserTierProgress(userId: string): Promise<TierProgressInfo> {
    const response = await this.request<{ progress: TierProgressInfo }>(
      'GET',
      `/api/users/${userId}/permissions`
    );
    return response.progress;
  }

  async upgradeTier(userId: string): Promise<TierUpgradeResponse> {
    return this.request<TierUpgradeResponse>('POST', `/api/users/${userId}/tier-upgrade`);
  }

  async getUserPermissions(userId: string): Promise<{
    tier: UserTier;
    role: string;
    progress: TierProgressInfo;
  }> {
    return this.request('GET', `/api/users/${userId}/permissions`);
  }

  // Organization Management
  async getOrganizations(limit = 50, cursor?: string): Promise<PaginatedResponse<Organization>> {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (cursor) params.append('cursor', cursor);

    return this.request<PaginatedResponse<Organization>>('GET', `/api/organizations?${params}`);
  }

  async getOrganization(orgId: string): Promise<Organization> {
    return this.request<Organization>('GET', `/api/organizations/${orgId}`);
  }

  async createOrganization(orgData: CreateOrganizationForm): Promise<Organization> {
    return this.request<Organization>('POST', '/api/organizations', orgData);
  }

  async getOrganizationMembers(orgId: string): Promise<{ members: User[] }> {
    return this.request('GET', `/api/organizations/${orgId}/members`);
  }

  async inviteUserToOrganization(
    orgId: string,
    email: string,
    role: 'user' | 'admin'
  ): Promise<{ success: boolean; message: string }> {
    return this.request('POST', `/api/organizations/${orgId}/invite`, { email, role });
  }

  // Verification
  async verifyEmail(data: {
    action?: 'send' | 'verify';
    email?: string;
    code?: string;
    verificationId?: string;
    template?: string;
  }): Promise<{
    success: boolean;
    message?: string;
    error?: string;
    verificationId?: string;
    expiresAt?: string;
    attempts?: number;
    maxAttempts?: number;
    canRetry?: boolean;
    verified?: boolean;
    verifiedAt?: string;
  }> {
    if (data.action === 'send') {
      return this.request('POST', '/api/auth/send-email-verification', {
        email: data.email,
        template: data.template || 'verification',
      });
    } else if (data.action === 'verify') {
      return this.request('POST', '/api/auth/verify-email', {
        verificationId: data.verificationId,
        code: data.code,
      });
    }
    throw new Error('Invalid action for email verification');
  }

  async verifyPhone(data: {
    action?: 'send' | 'verify';
    phoneNumber?: string;
    countryCode?: string;
    code?: string;
    verificationId?: string;
  }): Promise<{
    success: boolean;
    message?: string;
    error?: string;
    verificationId?: string;
    expiresAt?: string;
    maskedPhone?: string;
    attempts?: number;
    maxAttempts?: number;
    canRetry?: boolean;
    verified?: boolean;
    verifiedAt?: string;
  }> {
    if (data.action === 'send') {
      return this.request('POST', '/api/auth/send-phone-verification', {
        phoneNumber: data.phoneNumber,
        countryCode: data.countryCode,
      });
    } else if (data.action === 'verify') {
      return this.request('POST', '/api/auth/verify-phone', {
        verificationId: data.verificationId,
        code: data.code,
      });
    }
    throw new Error('Invalid action for phone verification');
  }

  async verifyIdentity(
    data: IdentityVerificationForm
  ): Promise<{ success: boolean; message: string }> {
    // Convert File objects to base64 for upload
    const formData = new FormData();
    formData.append('documentType', data.documentType);
    formData.append('documentNumber', data.documentNumber);
    formData.append('documentCountry', data.documentCountry);
    formData.append('fullName', data.fullName);
    formData.append('dateOfBirth', data.dateOfBirth);

    data.documentImages.forEach((file, index) => {
      formData.append(`documentImage${index}`, file);
    });

    if (data.photoImage) {
      formData.append('photoImage', data.photoImage);
    }

    try {
      const response = await axios.post(`${this.baseURL}/api/verify/identity`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(this.getAuthHeaders()['Authorization'] && {
            Authorization: this.getAuthHeaders()['Authorization'],
          }),
        },
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.error || error.message;
        throw new Error(message);
      }
      throw error;
    }
  }

  // Admin Functions
  async startImpersonation(targetUserId: string): Promise<{
    token: string;
    expiresAt: string;
    impersonatedUser: string;
  }> {
    return this.request('POST', `/api/admin/impersonate/${targetUserId}`);
  }

  async endImpersonation(sessionId: string): Promise<{ success: boolean }> {
    return this.request('DELETE', `/api/admin/impersonate/${sessionId}`);
  }

  async getAnalytics(): Promise<UserAnalytics> {
    return this.request<UserAnalytics>('GET', '/api/admin/analytics');
  }

  async getFeatureFlags(): Promise<{ featureFlags: FeatureFlag[] }> {
    return this.request('GET', '/api/admin/feature-flags');
  }

  async updateFeatureFlag(key: string, enabled: boolean): Promise<{ success: boolean }> {
    return this.request('PUT', `/api/admin/feature-flags/${key}`, { enabled });
  }

  // Rate Limiting Info
  async getRateLimitInfo(): Promise<{
    requestsPerMinute: number;
    requestsPerHour: number;
    requestsPerDay: number;
    currentMinute: number;
    currentHour: number;
    currentDay: number;
    resetTime: string;
  }> {
    return this.request('GET', '/api/rate-limit/info');
  }

  // Feature Access Check
  async checkFeatureAccess(featureKey: string): Promise<{ hasAccess: boolean; reason?: string }> {
    return this.request('GET', `/api/features/${featureKey}/access`);
  }

  // Bot Management (if needed for tier system)
  async getUserBots(userId: string): Promise<{ bots: any[] }> {
    return this.request('GET', `/api/users/${userId}/bots`);
  }

  async createBot(userId: string, botData: any): Promise<any> {
    return this.request('POST', `/api/users/${userId}/bots`, botData);
  }

  async updateBot(userId: string, botId: string, botData: any): Promise<any> {
    return this.request('PUT', `/api/users/${userId}/bots/${botId}`, botData);
  }

  async deleteBot(userId: string, botId: string): Promise<{ success: boolean }> {
    return this.request('DELETE', `/api/users/${userId}/bots/${botId}`);
  }

  // Payment Methods (if needed for tier system)
  async getPaymentMethods(userId: string): Promise<{ paymentMethods: any[] }> {
    return this.request('GET', `/api/users/${userId}/payment-methods`);
  }

  async addPaymentMethod(userId: string, paymentData: any): Promise<any> {
    return this.request('POST', `/api/users/${userId}/payment-methods`, paymentData);
  }

  async removePaymentMethod(
    userId: string,
    paymentMethodId: string
  ): Promise<{ success: boolean }> {
    return this.request('DELETE', `/api/users/${userId}/payment-methods/${paymentMethodId}`);
  }

  // Audit Logs
  async getAuditLogs(userId: string, limit = 50): Promise<any[]> {
    const params = new URLSearchParams({ limit: limit.toString() });
    return this.request('GET', `/api/users/${userId}/audit-logs?${params}`);
  }

  // Health Check
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    return this.request('GET', '/api/health');
  }
}

// Create singleton instance
const tierApiService = new TierApiService();

// Export composable
export function useTierApi() {
  return tierApiService;
}

export default tierApiService;
